import { useContext, useEffect, useMemo, useState } from 'react';
import api from '../api';
import './home.css';
import './newsFeed.css';
import { UserContext } from '../context/UserContext';
import { getSocialPlatforms } from '../utils/socialPlatformUtils';
import { resolveMediaUrl } from '../utils/url';

function formatWhen(it) {
  try {
    const raw = it?.publishedAt || it?.firstSeenAt;
    if (!raw) return '';
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

function platformLabel(platform) {
  const p = String(platform || '').toLowerCase();
  if (p === 'twitch') return 'Twitch';
  if (p === 'youtube') return 'YouTube';
  if (p === 'tiktok') return 'TikTok';
  if (p === 'instagram') return 'Instagram';
  return platform || 'Social';
}

function platformIcon(platform) {
  const p = String(platform || '').toLowerCase();
  if (p === 'twitch') return '🟣';
  if (p === 'youtube') return '▶️';
  if (p === 'tiktok') return '🎵';
  if (p === 'instagram') return '📸';
  return '🌐';
}

function normalizeThumb(u) {
  if (!u) return null;
  const s = String(u).trim();
  if (!s) return null;
  if (s.startsWith('http://') || s.startsWith('https://')) return s;
  if (s.startsWith('/')) return resolveMediaUrl(s);
  if (/^(?:api\/)?(avatars|uploads|social-content)\//i.test(s)) return resolveMediaUrl(`/${s}`);
  return `https://${s}`;
}

function findPlatformIconUrl(platformKey, platforms) {
  const key = String(platformKey || '').toLowerCase();
  if (!key) return null;
  const domainHint = {
    twitch: 'twitch.tv',
    youtube: 'youtube.com',
    tiktok: 'tiktok.com',
    instagram: 'instagram.com',
  };
  const list = Array.isArray(platforms) ? platforms : [];
  const byName = list.find((p) => String(p?.name || '').toLowerCase() === key);
  const hint = domainHint[key] || key;
  const byPattern = list.find((p) => String(p?.urlPattern || '').toLowerCase().includes(hint));
  const match = byName || byPattern;
  const icon = match?.icon;
  if (!icon) return null;
  if (String(icon).startsWith('http://') || String(icon).startsWith('https://') || String(icon).startsWith('data:')) return icon;
  return resolveMediaUrl(icon);
}

export default function NewsFeed() {
  const { user } = useContext(UserContext);
  const [items, setItems] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const [res, plats] = await Promise.all([
          api.get('/social-feed/latest', { params: { limit: 25 } }),
          getSocialPlatforms().catch(() => []),
        ]);
        const list = Array.isArray(res?.data?.items) ? res.data.items : [];
        if (mounted) {
          setItems(list);
          setPlatforms(Array.isArray(plats) ? plats : []);
        }
      } catch (e) {
        if (mounted) {
          setItems([]);
          setPlatforms([]);
          setError(e?.response?.data?.error || e?.message || 'News konnten nicht geladen werden.');
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const sorted = useMemo(() => {
    const list = Array.isArray(items) ? items : [];
    return [...list].sort((a, b) => {
      const at = new Date(a?.publishedAt || a?.firstSeenAt || 0).getTime();
      const bt = new Date(b?.publishedAt || b?.firstSeenAt || 0).getTime();
      if (bt !== at) return bt - at;
      return Number(b?.id || 0) - Number(a?.id || 0);
    });
  }, [items]);

  return (
    <div style={{ width: '100vw', margin: 0, padding: 0, display: 'flex', justifyContent: 'center' }}>
      <div className="home-page" style={{ padding: 32, maxWidth: 980, width: '100' + '%' }}>
        <section
          id="top"
          className={user ? 'hero-offset-if-sidebar' : ''}
          style={{
            width: '100vw',
            marginTop: 0,
            marginBottom: 0,
            padding: 0,
            marginLeft: 'calc(50% - 50vw)',
            marginRight: 'calc(50% - 50vw)',
          }}
        >
          <h2 className="hero-title" style={{ textAlign: 'center', margin: '0 0 8px' }}>Neuigkeiten</h2>
          <p className="hero-desc" style={{ margin: '0 auto 24px', textAlign: 'center', maxWidth: 760 }}>
            Die 25 neuesten Meldungen aus unserem Social‑Feed.
          </p>
        </section>

        <section style={{ width: '100%', maxWidth: 980, margin: '0 auto', padding: '0 0 40px 0' }}>
          {loading && (
            <div style={{ opacity: 0.8, padding: 16, textAlign: 'center' }}>
              <span style={{ fontSize: 22 }}>⏳</span>
              <div style={{ marginTop: 6 }}>Lade Neuigkeiten…</div>
            </div>
          )}

          {!loading && error && (
            <div style={{
              padding: 14,
              borderRadius: 12,
              border: '1px solid rgba(231, 76, 60, 0.55)',
              background: 'rgba(231, 76, 60, 0.12)',
              color: '#fff',
              marginBottom: 12,
            }}>
              {error}
            </div>
          )}

          {!loading && !error && sorted.length === 0 && (
            <div style={{ opacity: 0.8, padding: 16, textAlign: 'center' }}>
              Noch keine Meldungen vorhanden.
            </div>
          )}

          <div className="newsfeed-grid">
            {sorted.map((it) => {
              const url = it?.url && (String(it.url).startsWith('http://') || String(it.url).startsWith('https://'))
                ? it.url
                : (it?.url ? `https://${it.url}` : null);
              const thumb = normalizeThumb(it?.thumbnailUrl);
              const when = formatWhen(it);
              const plat = platformLabel(it?.platform);
              const platIcon = platformIcon(it?.platform);
              const platIconUrl = findPlatformIconUrl(it?.platform, platforms);
              const kind = String(it?.kind || '').trim();

              const card = (
                <div
                  style={{
                    background: 'rgba(26,43,74,0.85)',
                    borderRadius: 16,
                    boxShadow: '0 2px 12px rgba(44,99,214,0.10)',
                    padding: '14px 14px',
                    border: '1px solid rgba(255,255,255,0.10)',
                  }}
                >
                  {thumb ? (
                    <div style={{
                      width: '100%',
                      aspectRatio: '16 / 9',
                      borderRadius: 12,
                      overflow: 'hidden',
                      marginBottom: 10,
                      background: 'rgba(0,0,0,0.20)',
                      border: '1px solid rgba(255,255,255,0.10)',
                    }}>
                      <img
                        src={thumb}
                        alt="thumbnail"
                        loading="lazy"
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        onError={(e) => {
                          // hide broken thumbnails to keep layout clean
                          try {
                            e.currentTarget.style.display = 'none';
                          } catch {}
                        }}
                      />
                    </div>
                  ) : null}
                  <div style={{ fontWeight: 800, fontSize: 16, lineHeight: 1.35 }}>
                    {it?.text || ''}
                  </div>
                  <div style={{
                    marginTop: 8,
                    display: 'flex',
                    gap: 10,
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    opacity: 0.85,
                    fontSize: 13,
                  }}>
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '4px 10px',
                      borderRadius: 999,
                      background: 'rgba(255,255,255,0.08)',
                      border: '1px solid rgba(255,255,255,0.12)',
                      fontWeight: 800,
                    }}>
                      {platIconUrl ? (
                        <img
                          src={platIconUrl}
                          alt=""
                          aria-hidden="true"
                          style={{ width: 16, height: 16, borderRadius: 4, objectFit: 'cover', display: 'block' }}
                          loading="lazy"
                          onError={(e) => {
                            try {
                              e.currentTarget.style.display = 'none';
                            } catch {}
                          }}
                        />
                      ) : (
                        <span aria-hidden="true">{platIcon}</span>
                      )}
                      {plat}
                    </span>
                    {kind ? (
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '4px 10px',
                        borderRadius: 999,
                        background: 'rgba(255,255,255,0.06)',
                        border: '1px solid rgba(255,255,255,0.10)',
                        fontWeight: 700,
                      }}>
                        {kind}
                      </span>
                    ) : null}
                    {when ? <span style={{ color: '#D4D8DF' }}>🕒 {when}</span> : null}
                  </div>
                </div>
              );

              return url ? (
                <a
                  key={it?.id}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: 'none', color: 'inherit' }}
                  title="Link öffnen"
                >
                  {card}
                </a>
              ) : (
                <div key={it?.id}>{card}</div>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
}
